import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../shared/_services/gtw-snackbar.service';
import { JSONExportHeader, JSONExportService } from '../shared/_services/JSONExport.service';
import { GlobalService } from '../global/services/global.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { AppService } from '../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../shared/workspace-element-context';
import { NewUpdateScreenService } from './new-update-screen.service';

interface EnvironmentOption {
  label: string;
  value: string;
}

interface OracleTimestampPayload {
  length?: number;
  bytes?: string;
  null?: boolean;
  timeZone?: string;
}

@Component({
    selector: 'gtw-new-update-screen',
    templateUrl: './new-update-screen.component.html',
    styleUrls: ['./new-update-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NewUpdateScreenComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  environmentList: EnvironmentOption[] = [
    { label: 'Core', value: 'core' },
    { label: 'Client', value: 'client' }
  ];
  selectedEnv: EnvironmentOption = this.environmentList[0];
  tableName = '';

  isFetchLoading = false;
  isCreateLoading = false;
  isUpdateLoading = false;
  isDeleteLoading = false;
  isGetImpactedLoading = false;

  showNoDataMsg = false;
  showImpactedNoDataMsg = false;
  tableList: any[] = [];
  impactedTableList: any[] = [];
  impactedTableKeys: string[] = [];
  impactedRows: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: NewUpdateScreenService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonExportService: JSONExportService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const flatGp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);

    this.baseURLs = {
      api: this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api || shell.generalConfigData?.base_url || '',
      admin: this.GENERAL_CONFIG.admin_api_url || this.GENERAL_CONFIG.admin || shell.generalConfigData?.admin || '',
      custom: this.GENERAL_CONFIG.custom_api_url || this.GENERAL_CONFIG.custom || shell.generalConfigData?.custom || '',
      efile: this.GENERAL_CONFIG.efile_api_url || this.GENERAL_CONFIG.efile || '',
    };

    const baseGp = { ...extractObject(shell.globalParams), ...extractObject(flatGp) };
    const issueDefaults = this.gtwGlobal.isIssueIDEnabled(baseGp.tax_year, baseGp.scenario) || {};
    const mergedGp = { ...issueDefaults, ...baseGp };

    this.globalService = {
      ...shell,
      generalConfigData: this.GENERAL_CONFIG,
      globalParams: mergedGp,
    };

    this.cdr.markForCheck();
  }

  fetchTableDetails(): void {
    if (!String(this.tableName || '').trim()) {
      this.gtwSnackbarService.openSnackBar('Please enter a table name', 'gtw-snackbar--orange');
      this.cdr.markForCheck();
      return;
    }
    this.isFetchLoading = true;
    this.showNoDataMsg = false;
    this.showImpactedNoDataMsg = false;
    this.tableList = [];
    this.impactedTableList = [];
    this.impactedTableKeys = [];
    this.cdr.detectChanges();

    this.service.getTableDetails(
      this.baseURLs.api,
      this.selectedEnv.label,
      this.tableName
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isFetchLoading = false;
        if (response?.callSuccess === '1') {
          if (response?.jsonObject?.length > 0) {
            this.gtwSnackbarService.openSnackBar('Data fetched successfully', 'gtw-snackbar--green');
            this.tableList = response.jsonObject.map((item: any) => ({
              ...item,
              DATA_DEFAULT: item.DATA_DEFAULT || '',
              CONDITION_COLUMN: false,
              DATA_COLUMN: false,
              VALUE: ''
            }));
          } else {
            this.showNoDataMsg = true;
          }
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Unable to fetch data.', 'gtw-snackbar--red');
          this.showNoDataMsg = false;
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isFetchLoading = false;
        this.showNoDataMsg = false;
        this.gtwSnackbarService.openSnackBar('Unable to fetch data.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  fetchImpactedData(): void {
    this.isGetImpactedLoading = true;
    this.impactedTableList = [];
    this.impactedTableKeys = [];
    this.impactedRows = [];
    this.cdr.detectChanges();

    this.tableList.forEach((item: any) => {
      if (item.CONDITION_COLUMN) {
        this.impactedRows.push({
          COL_NAME: item.COLUMN_NAME,
          DATA_TYPE: item.DATA_TYPE,
          COL_VALUE: item.VALUE
        });
      }
    });

    const params = {
      table_name: this.tableName,
      json_obj: JSON.stringify(this.impactedRows)
    };

    this.service.getImpactedData(
      this.baseURLs.api,
      this.selectedEnv.label,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isGetImpactedLoading = false;
        if (response?.callSuccess === '1') {
          if (response?.jsonObject?.length > 0) {
            this.gtwSnackbarService.openSnackBar('Impacted data fetched successfully', 'gtw-snackbar--green');
            this.showImpactedNoDataMsg = false;
            this.impactedTableList = response.jsonObject.map((item: any) => {
              const updatedItem = { ...item };
              delete updatedItem.CREATED_ON;
              delete updatedItem.UPDATED_ON;
              Object.keys(updatedItem).forEach((key: string) => {
                updatedItem[key] = this.formatImpactedCellValue(updatedItem[key]);
              });
              return updatedItem;
            });
            this.impactedTableKeys = Object.keys(this.impactedTableList[0] || {});
          } else {
            this.showImpactedNoDataMsg = true;
          }
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Unable to fetch impacted data.', 'gtw-snackbar--red');
          this.showImpactedNoDataMsg = false;
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isGetImpactedLoading = false;
        this.showImpactedNoDataMsg = false;
        this.gtwSnackbarService.openSnackBar('Unable to fetch impacted data.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  performAction(actionType: string): void {
    if (actionType === 'Create') {
      for (let i = 0; i < this.tableList.length; i++) {
        if (this.tableList[i].DATA_DEFAULT === '' && this.tableList[i].VALUE === '') {
          this.gtwSnackbarService.openSnackBar(
            `All values are mandatory, ${this.tableList[i].COLUMN_NAME} cannot be null.`,
            'gtw-snackbar--yellow'
          );
          return;
        }
      }
    }

    if (actionType === 'Create') {
      this.isCreateLoading = true;
    } else if (actionType === 'Update') {
      this.isUpdateLoading = true;
    } else if (actionType === 'Delete') {
      this.isDeleteLoading = true;
    }
    this.cdr.detectChanges();

    const conditionCols: any[] = [];
    const dataCols: any[] = [];

    this.tableList.forEach((item: any) => {
      if (item.CONDITION_COLUMN) {
        conditionCols.push({
          COL_NAME: item.COLUMN_NAME,
          DATA_TYPE: item.DATA_TYPE,
          COL_VALUE: item.VALUE
        });
      }
      if (item.DATA_COLUMN) {
        dataCols.push({
          COL_NAME: item.COLUMN_NAME,
          DATA_TYPE: item.DATA_TYPE,
          COL_VALUE: item.VALUE
        });
      }
    });

    const payload = {
      table_name: this.tableName,
      json_obj: JSON.stringify({
        action_type: actionType,
        condition_cols: conditionCols,
        data_cols: dataCols,
        impacted_data: this.impactedTableList
      })
    };

    this.service.performAction(
      this.baseURLs.api,
      this.selectedEnv.label,
      payload,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isCreateLoading = false;
        this.isUpdateLoading = false;
        this.isDeleteLoading = false;
        if (response?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Action performed successfully', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Unable to perform action.', 'gtw-snackbar--red');
          this.showImpactedNoDataMsg = false;
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isCreateLoading = false;
        this.isUpdateLoading = false;
        this.isDeleteLoading = false;
        this.showImpactedNoDataMsg = false;
        this.gtwSnackbarService.openSnackBar('Unable to perform action.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  exportImpactedDataToExcel(): void {
    if (!this.impactedTableList?.length) {
      return;
    }

    const headersMap: JSONExportHeader[] = this.impactedTableKeys.map(key => ({
      field: key,
      header: key
    }));
    const fileName = `${this.tableName || 'impacted'}_impacted_${new Date().toISOString().slice(0, 10)}`;
    this.jsonExportService.exportToExcel(fileName, this.impactedTableList, headersMap);
  }

  trackByIndex(index: number): number {
    return index;
  }

  getImpactedValue(row: any, key: string): any {
    return this.formatImpactedCellValue(row[key]);
  }

  private formatImpactedCellValue(value: any): any {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    if (this.isOracleTimestampPayload(value)) {
      if (value.null) {
        return '';
      }
      const bytes = value.bytes?.trim();
      return bytes ? this.decodeOracleTimestampBytes(bytes) : '';
    }
    return value;
  }

  private isOracleTimestampPayload(value: any): value is OracleTimestampPayload {
    return value != null && typeof value === 'object' && typeof value.bytes === 'string';
  }

  private decodeOracleTimestampBytes(base64: string): string {
    try {
      const binary = atob(base64);
      if (binary.length < 7) {
        return '';
      }
      const b = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        b[i] = binary.charCodeAt(i);
      }
      const year = (b[0] - 100) * 100 + (b[1] - 100);
      const month = b[2];
      const day = b[3];
      const hour = b[4] - 1;
      const minute = b[5] - 1;
      const second = b[6] - 1;
      const pad2 = (n: number) => String(n).padStart(2, '0');
      let millis = 0;
      if (b.length >= 11) {
        millis = Math.floor(
          ((b[7] << 24) | (b[8] << 16) | (b[9] << 8) | b[10]) / 1_000_000,
        );
      }
      return `${year}-${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}:${pad2(second)}.${String(millis).padStart(3, '0')}`;
    } catch {
      return '';
    }
  }

  compareEnv(env1: EnvironmentOption, env2: EnvironmentOption): boolean {
    return env1 && env2 ? env1.value === env2.value : env1 === env2;
  }
}
